import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { ActionSurface } from '@/components/patterns/action-surface'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { AddressFormatter } from '@/components/ui/address-formatter'
import { Typography } from '@/components/ui/typography'
import { Country } from '@/lib/enums/Country'

export function AddressVerificationDialog({
  open,
  title = 'Confirm the delivery address',
  description = 'We found a close match for this address. Choose which address to use.',
  typedAddress = {},
  suggested,
  isSuggestedUsable = false,
  defaultCountry = Country.UnitedStates.Value,
  onChooseTyped,
  onChooseSuggested,
  onCancel,
}) {
  const suggestedLine1 =
    suggested &&
    ([suggested.street_number, suggested.route].filter(Boolean).join(' ').trim() ||
      suggested.formattedAddress ||
      '')

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent
        showCloseButton={false}
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <ActionSurface
            onClick={onChooseTyped}
            className="w-full flex-col gap-2"
            aria-label="Your address"
          >
            <Typography variant="label">Your Address</Typography>
            <Typography as="div" variant="bodySmall" tone="muted">
              <AddressFormatter
                variant="full"
                address={{
                  line1: typedAddress?.line1 || '',
                  line2: typedAddress?.line2 || '',
                  city: typedAddress?.city || '',
                  state: typedAddress?.state || '',
                  postal_code: typedAddress?.postal_code || '',
                  country: typedAddress?.country || defaultCountry,
                }}
              />
            </Typography>
          </ActionSurface>

          <ActionSurface
            disabled={!isSuggestedUsable}
            onClick={() => {
              if (!isSuggestedUsable) return
              onChooseSuggested?.()
            }}
            className="relative w-full flex-col gap-2"
            aria-label="Use suggested address"
          >
            <span className="flex w-full items-center justify-between gap-2">
              <Typography variant="label">Suggested Address</Typography>
              <Badge variant="info" size="xs">
                Recommended
              </Badge>
            </span>
            <Typography as="div" variant="bodySmall" tone="muted">
              <AddressFormatter
                variant="full"
                address={{
                  line1: suggestedLine1,
                  line2: suggested?.subpremise ?? '',
                  city: suggested?.locality || suggested?.neighborhood || '',
                  state: suggested?.administrative_area_level_1 || '',
                  postal_code: suggested?.postal_code || '',
                  country: suggested?.country || defaultCountry,
                }}
              />
            </Typography>
            {!isSuggestedUsable && (
              <Typography variant="caption">
                No verified suggestion available. Please use the address as entered.
              </Typography>
            )}
          </ActionSurface>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
